/* eslint-disable react-refresh/only-export-components */
import { Plus, RotateCcw, Save } from "lucide-react";
import type { RaRecord } from "ra-core";
import {
  EditBase,
  Form,
  useGetList,
  useInput,
  useNotify,
  useSimpleFormIterator,
  useSourceContext,
  useTranslate,
} from "ra-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toSlug } from "@/lib/toSlug";
import { ArrayInput } from "@/components/admin/array-input";
import { Confirm } from "@/components/admin/confirm";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import ImageEditorField from "../misc/ImageEditorField";
import { PlanUsageCard } from "./PlanUsageCard";
import { TIMEZONE_CHOICES, gmtOffsetLabel } from "../misc/DateTimeStamp";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";
import type { CustomFieldDef } from "../types";

const SECTIONS = [
  {
    id: "branding",
    label: "crm.settings.sections.branding",
    fallback: "Branding",
  },
  {
    id: "profile",
    label: "crm.settings.sections.profile",
    fallback: "Company profile",
  },
  { id: "plan", label: "crm.settings.sections.plan", fallback: "Your plan" },
  {
    id: "companies",
    label: "resources.companies.name",
    fallback: "Companies",
  },
  { id: "deals", label: "resources.deals.name", fallback: "Deals" },
  { id: "notes", label: "resources.notes.name", fallback: "Notes" },
  { id: "timezone", label: "crm.settings.sections.timezone", fallback: "Timezone" },
  { id: "lifecycle", label: "crm.settings.sections.lifecycle", fallback: "Lifecycle" },
  { id: "tasks", label: "resources.tasks.name", fallback: "Tasks" },
  {
    id: "customFields",
    label: "crm.settings.sections.custom_fields",
    fallback: "Custom fields",
  },
];

/** Ensure every item in a { value, label } array has a value (slug from label). */
const ensureValues = (items: { value?: string; label: string }[] | undefined) =>
  items?.map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Check for duplicate slugs
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["en"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

/**
 * BUG: this only ever built a config object from the ~14 fields this form's
 * inputs actually track - moduleFlags, workflows, campaigns, sequences,
 * forms, integrations, scoringRules, dashboards, emailTemplates, and every
 * other builder's own tenant content were never in this list. The mutation
 * sends whatever `transform` returns as the ENTIRE new `config` jsonb value
 * (updateConfiguration does a plain column replace, not a merge) - so every
 * save through this page silently wiped all of that from the database, even
 * though `onSuccess` below merges onto the full config for the LOCAL cache
 * (masking the loss until the next real page load/getOne, which is exactly
 * what made "Inbox/Reports are visible again after I turned them off"
 * reproduce: the toggle write and the very next Settings save raced, and the
 * save always won). Fixed by spreading the full current `config` FIRST, so
 * an untracked field only ever gets overwritten by an explicit key below,
 * never dropped by omission - the same pattern every other builder
 * (WorkflowsTab, CampaignsTab, etc.) already uses for its own save.
 */
const buildTransformFormValues =
  (config: ConfigurationContextValue) => (data: Record<string, any>) => ({
    config: {
      ...config,
      title: data.title,
      lightModeLogo: data.lightModeLogo,
      darkModeLogo: data.darkModeLogo,
      currency: data.currency,
      companySectors: ensureValues(data.companySectors),
      dealCategories: ensureValues(data.dealCategories),
      taskTypes: ensureValues(data.taskTypes),
      dealStages: ensureValues(data.dealStages),
      dealPipelineStatuses: data.dealPipelineStatuses,
      noteStatuses: ensureValues(data.noteStatuses),
      timezone: data.timezone,
      companyProfile: data.companyProfile,
      lifecycleStages: ensureValues(data.lifecycleStages),
      customFields: data.customFields?.map(
        (field: { key?: string; label: string }) => ({
          ...field,
          key: field.key || toSlug(field.label),
        }),
      ),
    } as ConfigurationContextValue,
  });

/** Which cards each settings-hub page shows. No prop = legacy full page. */
const SECTION_CARDS: Record<string, string[]> = {
  "account-defaults": ["branding", "profile", "timezone"],
  billing: ["plan"],
  objects: ["companies", "deals", "notes", "tasks"],
  lifecycle: ["lifecycle"],
  "custom-fields": ["customFields"],
};

/**
 * Which top-level form fields each card actually edits - matches the
 * `source` props inside that Card below. Used to scope Save and Reset to
 * defaults to just the current section's own fields, now that each section
 * has its own action bar instead of one global one covering every card on
 * the page (decision: "each section should have its own modal for saving").
 */
const CARD_FIELDS: Record<string, string[]> = {
  branding: ["title", "lightModeLogo", "darkModeLogo"],
  profile: ["companyProfile"],
  plan: [],
  companies: ["companySectors"],
  deals: ["currency", "dealStages", "dealPipelineStatuses", "dealCategories"],
  notes: ["noteStatuses"],
  timezone: ["timezone"],
  lifecycle: ["lifecycleStages"],
  tasks: ["taskTypes"],
  customFields: ["customFields"],
};

/** Same special-casing the old whole-page reset used for the two logo fields. */
const fieldDefault = (field: string): unknown => {
  if (field === "lightModeLogo") return { src: defaultConfiguration.lightModeLogo };
  if (field === "darkModeLogo") return { src: defaultConfiguration.darkModeLogo };
  return (defaultConfiguration as unknown as Record<string, unknown>)[field];
};

export const SettingsPage = ({ section }: { section?: string } = {}) => {
  const updateConfiguration = useConfigurationUpdater();
  const config = useConfigurationContext();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={buildTransformFormValues(config)}
      mutationOptions={{
        onSuccess: (data: any) => {
          // The transform above already merges onto the full config before
          // the write happens - this local-cache merge just keeps the two
          // in sync (belt and suspenders), it's no longer the only thing
          // standing between a save and losing every other builder's data.
          updateConfiguration({ ...config, ...data.config });
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm section={section} />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = ({ section }: { section?: string }) => {
  const config = useConfigurationContext();

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      currency: config.currency,
      companySectors: config.companySectors,
      dealCategories: config.dealCategories,
      taskTypes: config.taskTypes,
      dealStages: config.dealStages,
      dealPipelineStatuses: config.dealPipelineStatuses,
      noteStatuses: config.noteStatuses,
      timezone: config.timezone,
      companyProfile: config.companyProfile,
      lifecycleStages: config.lifecycleStages,
      customFields: config.customFields,
    }),
    [config],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields section={section} />
    </Form>
  );
};

/**
 * Options only means anything for a "select" field - showing a blank,
 * unexplained input for every text/number/date row was confusing (Michael:
 * "maybe we don't need options? no idea what it does"), and reserving that
 * width in every row left too little room for the label next to it. Reads
 * this row's OWN type via the array item's scoped source, so it only
 * appears once "Select" is actually chosen for that row.
 */
const CustomFieldOptionsInput = () => {
  const { getSource } = useSourceContext();
  const type = useWatch({ name: getSource("type") });
  if (type !== "select") return null;
  return (
    <TextInput
      source="options"
      label="Options"
      className="w-48 shrink-0"
      helperText="Comma-separated choices"
    />
  );
};

/** Same row-scoped-watch pattern as CustomFieldOptionsInput, for a "number" field's display format. */
const CustomFieldFormatInput = () => {
  const { getSource } = useSourceContext();
  const type = useWatch({ name: getSource("type") });
  if (type !== "number") return null;
  return (
    <SelectInput
      source="format"
      label="Format"
      className="w-36 shrink-0"
      choices={[
        { id: "plain", name: "Plain" },
        { id: "currency", name: "Currency ($)" },
      ]}
      defaultValue="plain"
      helperText={false}
    />
  );
};

/**
 * Adding a field used to mean an inline blank row appended at the bottom of
 * an already-long settings page (Michael: "that should open a modal, rather
 * than adding the banner on the bottom of the screen"). This replaces
 * SimpleFormIterator's default add button: same "+", but it collects the
 * new field in a dialog and hands the FINISHED item to the iterator's own
 * `add()` in one shot, instead of appending a blank row to fill in place.
 */
const AddCustomFieldButton = () => {
  const { add } = useSimpleFormIterator();
  const existing = (useWatch({ name: "customFields" }) as CustomFieldDef[] | undefined) ?? [];

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [object, setObject] = useState<CustomFieldDef["object"]>("contacts");
  const [type, setType] = useState<CustomFieldDef["type"]>("text");
  const [options, setOptions] = useState("");
  const [format, setFormat] = useState<NonNullable<CustomFieldDef["format"]>>("plain");

  const key = toSlug(label);
  const duplicate =
    key !== "" && existing.some((f) => f.object === object && (f.key ?? "") === key);

  const reset = () => {
    setLabel("");
    setObject("contacts");
    setType("text");
    setOptions("");
    setFormat("plain");
  };

  const handleAdd = () => {
    if (!key || duplicate) return;
    add({
      key,
      label: label.trim(),
      object,
      type,
      ...(type === "select" ? { options } : {}),
      ...(type === "number" ? { format } : {}),
    });
    reset();
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-4 w-4" /> Add custom field
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New custom field</DialogTitle>
            <DialogDescription>
              Appears on the create and edit forms of the chosen object, for
              every user in this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div>
              <Label htmlFor="new-custom-field-label" className="mb-1">
                Field name
              </Label>
              <Input
                id="new-custom-field-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                autoFocus
              />
              {duplicate && (
                <p className="text-xs text-destructive mt-1">
                  A field with this name already exists for this object.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1">Applies to</Label>
                <Select
                  value={object}
                  onValueChange={(v: CustomFieldDef["object"]) => setObject(v)}
                >
                  <SelectTrigger className="w-full" aria-label="Applies to">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contacts">Contacts</SelectItem>
                    <SelectItem value="companies">Companies</SelectItem>
                    <SelectItem value="deals">Deals</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1">Field type</Label>
                <Select
                  value={type}
                  onValueChange={(v: CustomFieldDef["type"]) => setType(v)}
                >
                  <SelectTrigger className="w-full" aria-label="Field type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="select">Select</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {type === "select" && (
              <div>
                <Label htmlFor="new-custom-field-options" className="mb-1">
                  Choices
                </Label>
                <Input
                  id="new-custom-field-options"
                  value={options}
                  onChange={(e) => setOptions(e.target.value)}
                  placeholder="Comma-separated choices"
                />
              </div>
            )}
            {type === "number" && (
              <div>
                <Label className="mb-1">Number format</Label>
                <Select
                  value={format}
                  onValueChange={(v: NonNullable<CustomFieldDef["format"]>) =>
                    setFormat(v)
                  }
                >
                  <SelectTrigger className="w-full" aria-label="Number format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plain">Plain number</SelectItem>
                    <SelectItem value="currency">Currency ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={!key || duplicate} onClick={handleAdd}>
              Add field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SettingsFormFields = ({ section }: { section?: string }) => {
  const translate = useTranslate();
  const visible = useMemo(
    () => (section ? SECTION_CARDS[section] ?? [] : null),
    [section],
  );
  const cardCls = (id: string) => (visible && !visible.includes(id) ? "hidden" : undefined);
  const sectionFields = useMemo(
    () => (visible ?? []).flatMap((id) => CARD_FIELDS[id] ?? []),
    [visible],
  );
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  const {
    watch,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
  } = useFormContext();

  // A successful save doesn't otherwise clear react-hook-form's own dirty
  // tracking (it's relative to the form's ORIGINAL defaultValues, which
  // this component never updates after mount) - without this, the Save
  // button stayed enabled and looked like the edit was still pending even
  // right after "Configuration saved successfully".
  useEffect(() => {
    if (isSubmitSuccessful) reset(getValues());
  }, [isSubmitSuccessful, reset, getValues]);

  // Each section now saves and resets on its own, through a confirm modal,
  // instead of one global fixed bar governing the whole page (Michael: "each
  // section should have its own modal for saving, similar to the custom
  // property"). Reset is scoped to just this section's own fields
  // (sectionFields/fieldDefault above) - the old version wiped EVERY
  // setting on the page with no confirmation at all. Save still submits the
  // whole form (there's only one underlying config object/mutation), but in
  // practice a section is always visited on its own via a fresh route, so
  // there's nothing else dirty to carry along.
  const [confirmAction, setConfirmAction] = useState<"reset" | "save" | null>(null);
  const formNode = useRef<HTMLFormElement | null>(null);

  const dealStages = watch("dealStages");
  const dealPipelineStatuses: string[] = watch("dealPipelineStatuses") ?? [];
  const stageDisplayName = translate("crm.settings.validation.entities.stages");
  const categoryDisplayName = translate(
    "crm.settings.validation.entities.categories",
  );

  const { data: deals } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const validateDealStages = useCallback(
    (stages: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(stages, deals, "stage", stageDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
        inUse: (displayName, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: displayName,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [deals, stageDisplayName, translate],
  );

  const validateDealCategories = useCallback(
    (categories: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(categories, deals, "category", categoryDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
        inUse: (displayName, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: displayName,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [categoryDisplayName, deals, translate],
  );

  return (
    <div className="flex gap-8 mt-4 pb-8">
      {/* Left navigation (legacy full-page view only - the hub has its own rail) */}
      <nav className={section ? "hidden" : "hidden md:block w-48 shrink-0"}>
        <div className="sticky top-4 space-y-1">
          <h1 className="text-2xl font-semibold px-3 mb-2">
            {translate("crm.settings.title")}
          </h1>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                document
                  .getElementById(section.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors"
            >
              {translate(section.label, { smart_count: 2, _: section.fallback })}
            </button>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-2xl space-y-6">
        {/* Branding */}
        <Card id="branding" className={cardCls("branding")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.branding")}
            </h2>
            <TextInput source="title" label="crm.settings.app_title" />
            <div className="flex gap-8">
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.light_mode_logo")}
                </p>
                <ImageEditorField
                  source="lightModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#f5f5f5"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.dark_mode_logo")}
                </p>
                <ImageEditorField
                  source="darkModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#1a1a1a"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company profile */}
        <Card id="profile" className={cardCls("profile")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              Company profile
            </h2>
            <p className="text-sm text-muted-foreground">
              Who this workspace belongs to. Shows up in email footers,
              exports, and anywhere Obvious speaks for your company.
            </p>
            <TextInput source="companyProfile.companyName" label="Company name" />
            <TextInput source="companyProfile.websiteUrl" label="Website" />
            <div className="grid sm:grid-cols-2 gap-4">
              <TextInput source="companyProfile.industry" label="Industry" />
              <TextInput source="companyProfile.phone" label="Phone" />
            </div>
          </CardContent>
        </Card>

        {/* Your plan */}
        <div className={cardCls("plan")}>
          <PlanUsageCard />
        </div>

        {/* Companies */}
        <Card id="companies" className={cardCls("companies")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.companies.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.companies.sectors")}
            </h3>
            <ArrayInput
              source="companySectors"
              label={false}
              helperText={false}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Deals */}
        <Card id="deals" className={cardCls("deals")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.deals.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.currency")}
            </h3>
            <AutocompleteInput
              source="currency"
              label={false}
              choices={currencyChoices}
              inputText={(choice) => choice?.id}
              modal
            />

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.stages")}
            </h3>
            <ArrayInput
              source="dealStages"
              label={false}
              helperText={false}
              validate={validateDealStages}
            >
              <SimpleFormIterator disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.pipeline_statuses")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.deals.pipeline_help")}
            </p>
            <div className="flex flex-wrap gap-2">
              {dealStages?.map(
                (stage: { value: string; label: string }, idx: number) => {
                  const isSelected = dealPipelineStatuses.includes(stage.value);
                  return (
                    <Button
                      key={idx}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (isSelected) {
                          setValue(
                            "dealPipelineStatuses",
                            dealPipelineStatuses.filter(
                              (s) => s !== stage.value,
                            ),
                          );
                        } else {
                          setValue("dealPipelineStatuses", [
                            ...dealPipelineStatuses,
                            stage.value,
                          ]);
                        }
                      }}
                    >
                      {stage.label || stage.value}
                    </Button>
                  );
                },
              )}
            </div>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.categories")}
            </h3>
            <ArrayInput
              source="dealCategories"
              label={false}
              helperText={false}
              validate={validateDealCategories}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card id="notes" className={cardCls("notes")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.notes.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.notes.statuses")}
            </h3>
            <ArrayInput source="noteStatuses" label={false} helperText={false}>
              <SimpleFormIterator inline disableReordering disableClear>
                <TextInput source="label" label={false} className="flex-1" />
                <ColorInput source="color" />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Timezone */}
        <Card id="timezone" className={cardCls("timezone")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              Timezone
            </h2>
            <p className="text-sm text-muted-foreground">
              Every logged note, call, meeting, and email shows its date and
              time in this zone (defaults to Eastern).
            </p>
            <SelectInput
              source="timezone"
              label={false}
              helperText={false}
              choices={TIMEZONE_CHOICES.map((t) => ({
                id: t.id,
                name: `${t.name} · ${gmtOffsetLabel(t.id)}`,
              }))}
            />
          </CardContent>
        </Card>

        {/* Lifecycle */}
        <Card id="lifecycle" className={cardCls("lifecycle")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              Lifecycle stages
            </h2>
            <p className="text-sm text-muted-foreground">
              The funnel ladder every contact climbs - ordered top to bottom.
              Feeds the funnel chart, lists, and workflow conditions.
            </p>
            <ArrayInput source="lifecycleStages" label={false} helperText={false}>
              <SimpleFormIterator inline disableReordering disableClear>
                <TextInput source="label" label={false} className="flex-1" />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card id="tasks" className={cardCls("tasks")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.tasks.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.tasks.types")}
            </h3>
            <ArrayInput source="taskTypes" label={false} helperText={false}>
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Custom fields */}
        <Card id="customFields" className={cardCls("customFields")}>
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.custom_fields", {
                _: "Custom fields",
              })}
            </h2>
            <p className="text-sm text-muted-foreground">
              Fields defined here appear on the create and edit forms of the
              chosen object. "Options" (for Select fields) and "Format" (for
              Number fields, to show it as currency) only show up once
              they're relevant.
            </p>
            <ArrayInput source="customFields" label={false} helperText={false}>
              <SimpleFormIterator
                inline
                disableReordering
                disableClear
                addButton={<AddCustomFieldButton />}
              >
                {/* One flex-wrap group instead of relying on the iterator's
                    own single-line row - Options and (now) Format mean up
                    to 5 inputs can be visible at once, which no longer fits
                    on one line without spilling past the card's edge. */}
                <div className="flex flex-wrap gap-2 flex-1 min-w-0">
                  <TextInput
                    source="label"
                    label="Label"
                    className="flex-1 min-w-40"
                  />
                  <SelectInput
                    source="object"
                    label="Object"
                    className="w-36 shrink-0"
                    choices={[
                      { id: "contacts", name: "Contacts" },
                      { id: "companies", name: "Companies" },
                      { id: "deals", name: "Deals" },
                    ]}
                    defaultValue="contacts"
                  />
                  <SelectInput
                    source="type"
                    label="Type"
                    className="w-32 shrink-0"
                    choices={[
                      { id: "text", name: "Text" },
                      { id: "number", name: "Number" },
                      { id: "date", name: "Date" },
                      { id: "select", name: "Select" },
                    ]}
                    defaultValue="text"
                  />
                  <CustomFieldOptionsInput />
                  <CustomFieldFormatInput />
                </div>
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {sectionFields.length > 0 && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <Confirm
              isOpen={confirmAction === "reset"}
              title="Reset this section to defaults?"
              content="This replaces this section's own fields with their factory defaults - not the rest of the settings page. It only takes effect once you Save, but there's no undo after that."
              confirm="Reset section"
              confirmColor="warning"
              onConfirm={() => {
                reset({
                  ...getValues(),
                  ...Object.fromEntries(
                    sectionFields.map((f) => [f, fieldDefault(f)]),
                  ),
                });
                setConfirmAction(null);
              }}
              onClose={() => setConfirmAction(null)}
            />
            <Confirm
              isOpen={confirmAction === "save"}
              title="Save these changes?"
              content="This updates the settings for everyone in this workspace."
              confirm={translate("ra.action.save")}
              onConfirm={() => {
                formNode.current?.requestSubmit();
                setConfirmAction(null);
              }}
              onClose={() => setConfirmAction(null)}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmAction("reset")}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {translate("crm.settings.reset_defaults")}
            </Button>
            <Button
              type="button"
              disabled={isSubmitting || !isDirty}
              onClick={(e) => {
                formNode.current = e.currentTarget.closest("form");
                setConfirmAction("save");
              }}
            >
              <Save className="h-4 w-4 mr-1" />
              {isSubmitting
                ? translate("crm.settings.saving")
                : translate("ra.action.save")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};
